import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Divider variants using class-variance-authority
 */
const dividerVariants = cva('shrink-0 bg-border', {
  variants: {
    orientation: {
      horizontal: 'h-[1px] w-full',
      vertical: 'h-full w-[1px]',
    },
    variant: {
      default: '',
      dashed: 'border-dashed',
      dotted: 'border-dotted',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    variant: 'default',
  },
});

export interface DividerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof dividerVariants> {
  /**
   * Text label to display in the center of the divider
   */
  label?: string;
  /**
   * Label position
   */
  labelPosition?: 'left' | 'center' | 'right';
}

/**
 * Divider Component
 *
 * A visual separator for content sections.
 * Supports horizontal and vertical orientations with optional labels.
 *
 * @example
 * // Basic horizontal divider
 * <Divider />
 *
 * @example
 * // Divider with label
 * <Divider label="OR" />
 * <Divider label="Patient Information" labelPosition="left" />
 *
 * @example
 * // Vertical divider
 * <div className="flex h-20">
 *   <div>Left content</div>
 *   <Divider orientation="vertical" />
 *   <div>Right content</div>
 * </div>
 *
 * @example
 * // Dashed divider
 * <Divider variant="dashed" />
 */
const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
  (
    {
      className,
      orientation = 'horizontal',
      variant,
      label,
      labelPosition = 'center',
      ...props
    },
    ref,
  ) => {
    // If there's a label and orientation is horizontal, render with label
    if (label && orientation === 'horizontal') {
      return (
        <div
          ref={ref}
          className={cn('flex items-center gap-3', className)}
          role="separator"
          aria-orientation={orientation}
          {...props}
        >
          {(labelPosition === 'center' || labelPosition === 'right') && (
            <div className={cn(dividerVariants({ orientation, variant }), 'flex-1')} />
          )}
          <span className="text-sm text-muted-foreground">{label}</span>
          {(labelPosition === 'center' || labelPosition === 'left') && (
            <div className={cn(dividerVariants({ orientation, variant }), 'flex-1')} />
          )}
        </div>
      );
    }

    // Regular divider without label
    return (
      <div
        ref={ref}
        className={cn(dividerVariants({ orientation, variant }), className)}
        role="separator"
        aria-orientation={orientation || undefined}
        {...props}
      />
    );
  },
);
Divider.displayName = 'Divider';

/**
 * Divider with spacing
 */
export interface SpacedDividerProps extends DividerProps {
  /**
   * Vertical spacing around the divider (Tailwind spacing value)
   */
  spacing?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * SpacedDivider Component
 *
 * A divider with built-in vertical spacing.
 *
 * @example
 * <SpacedDivider spacing="lg" />
 * <SpacedDivider spacing="md" label="Section Break" />
 */
const SpacedDivider = React.forwardRef<HTMLDivElement, SpacedDividerProps>(
  ({ spacing = 'md', className, ...props }, ref) => {
    const spacingClass = {
      sm: 'my-2',
      md: 'my-4',
      lg: 'my-6',
      xl: 'my-8',
    }[spacing];

    return (
      <Divider
        ref={ref}
        className={cn(spacingClass, className)}
        {...props}
      />
    );
  },
);
SpacedDivider.displayName = 'SpacedDivider';

export { Divider, SpacedDivider, dividerVariants };
